import { Fragment, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowLeftIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { CurationPhotosetSpace } from '@/features/photoset/components/curation-photoset-space'
import { GalleryCurationModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-curation-modal/gallery-curation-modal'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { SubHeaderBanner } from '@/features/photoset/components/sub-header-banner'
import { UnusableCurationPhotosetSpace } from '@/features/photoset/components/unusable-curation-photoset-space/unusable-curation-photoset-space'
import { useExpandPhotosetImage } from '@/features/photoset/hooks/use-expand-photoset-image'
import { useGetFlags } from '@/features/photoset/queries/use-get-flags'
import { useGetPhotosetSpaces } from '@/features/photoset/queries/use-get-photoset-spaces'
import { useUpdateStepToAllocation } from '@/features/photoset/queries/use-update-step-to-allocation'
import { useUpdateStepToCompleted } from '@/features/photoset/queries/use-update-step-to-completed'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import { errorHandler, objectKeys, objectValues } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetCurationStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [hideAllUnselected, setHideAllUnselected] = useState(false)

	const {
		data: spaces,
		isFetching,
		isError
	} = useGetPhotosetSpaces({
		urlParams: {
			photosetId: urlParams.id
		},
		params: { response_type: 'extended' }
	})

	useGetFlags(
		{
			params: {
				types: ['metadata', 'visual_metadata', 'visual'],
				response_type: 'extended'
			}
		},
		{ staleTime: Number.POSITIVE_INFINITY }
	)

	//photoset store
	const initPhotosetSpaces = usePhotosetStore(
		(state) => state.initPhotosetSpaces
	)
	const photosetSpaces = usePhotosetStore((state) => state.photosetSpaces)
	const photosetSpacesChecks = usePhotosetStore(
		(state) => state.photosetSpacesChecks
	)
	const resetPhotosetSpaces = usePhotosetStore(
		(state) => state.resetPhotosetSpaces
	)

	//checks
	const errors = useMemo(() => {
		const flagsRequired = Object.values(photosetSpacesChecks).some(
			(checks) =>
				checks.missingFlags &&
				checks.selectedCounter < checks.minimumSelection
		)

		const mimimumSelectionErrors = Object.values(photosetSpacesChecks).some(
			(checks) => checks.selectedCounter < checks.minimumSelection
		)

		const spaceFlagRequired = Object.values(photosetSpacesChecks).some(
			(checks) => checks.spaceFlagRequired
		)

		return {
			spaceFlagRequired: spaceFlagRequired
				? t('photoset:curation.space_error_subtitle')
				: null,
			mimimumSelectionErrors:
				mimimumSelectionErrors && flagsRequired
					? t('photoset:curation.selection_error_subtitle', {
							intro: spaceFlagRequired
								? t('common:also')
								: t('common:please')
						})
					: null,
			flagsRequired: flagsRequired
				? t('photoset:curation.flags_error_subtitle', {
						intro:
							spaceFlagRequired || mimimumSelectionErrors
								? t('common:also')
								: t('common:please')
					})
				: null
		}
	}, [photosetSpacesChecks, t])

	const showErrorBanner = useMemo(
		() => Boolean(objectValues(errors).some((err) => err !== null)),
		[errors]
	)

	//state
	const [curationTouched, setCurationTouched] = useState(false)
	//mutations
	const updateStepToAllocationMutation = useUpdateStepToAllocation({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			resetPhotosetSpaces()
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.IMAGES]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
		}
	})
	const updateStepToCompleted = useUpdateStepToCompleted({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.IMAGES]
			})
		}
	})

	const onCurationCompleted = () => {
		setCurationTouched(true)
		const finalSelectedImages: number[] = []
		if (!showErrorBanner) {
			photosetSpaces.map((spaces) =>
				spaces.images.map((image) => {
					if (image.is_final_select) {
						finalSelectedImages.push(image.id)
					}
					return image
				})
			)
			updateStepToCompleted.mutate({
				photosetId: urlParams.id,
				imagesIds: finalSelectedImages
			})
		}
	}

	const onHideAllUnselectedCb = () => {
		setHideAllUnselected(!hideAllUnselected)
	}

	//init Photoset Spaces
	useEffect(() => {
		if (spaces) {
			initPhotosetSpaces(spaces)
		}
	}, [spaces, initPhotosetSpaces])

	// Handle gallery modal view
	const {
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		currentDetailSpace,
		onExpandImage,
		onReduceImage
	} = useExpandPhotosetImage()

	return (
		<>
			<PageHero
				endSlot={
					<div className='flex items-center gap-4'>
						<Switch
							checked={hideAllUnselected}
							label={t('photoset:curation.hide_unselected')}
							onChange={onHideAllUnselectedCb}
						/>
						<StepHeroControls hideSortControls />
					</div>
				}
				subtitle={t('photoset:curation.subtitle')}
				title={t('photoset:curation.title')}
			/>
			{showErrorBanner && curationTouched && (
				<SubHeaderBanner
					subtitle={objectKeys(errors)
						.filter((error) => !!errors[error])
						.map((error) => errors[error])
						.join(' ')}
					title={t('photoset:curation.selection_error_title')}
					type={'danger'}
				/>
			)}

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer
					$disabled={updateStepToCompleted.isPending}
				>
					{isFetching &&
						[0, 1, 2].map((e) => (
							<CurationPhotosetSpace asSkeleton key={`${e}-id`} />
						))}
					{!(isFetching || isError) &&
						photosetSpaces?.map((photosetSpace, spaceIndex) => (
							<Fragment key={photosetSpace.id}>
								{photosetSpacesChecks[spaceIndex]
									.unusableSpace ? (
									<UnusableCurationPhotosetSpace
										cbSetHideAllUnselected={
											onHideAllUnselectedCb
										}
										curationTouched={curationTouched}
										images={photosetSpace.images}
										onExpandImage={onExpandImage}
										spaceChecks={
											photosetSpacesChecks[spaceIndex]
										}
										spaceConfigs={
											photosetSpace.configuration_space
										}
										spaceFlagValue={
											photosetSpace.flag_key_name
										}
										spaceId={photosetSpace.id}
										spaceIndex={spaceIndex}
										spaceName={photosetSpace.space.key_name}
										totalImages={
											photosetSpace.images.length
										}
										type={
											photosetSpace.images.length === 0
												? 'noImages'
												: 'minimumNotMet'
										}
									/>
								) : (
									<CurationPhotosetSpace
										cbSetHideAllUnselected={
											onHideAllUnselectedCb
										}
										curationTouched={curationTouched}
										currentDetailImageIndex={
											currentDetailImageIndex
										}
										hideAllUnselected={hideAllUnselected}
										images={photosetSpace.images}
										onExpandImage={onExpandImage}
										spaceChecks={
											photosetSpacesChecks[spaceIndex]
										}
										spaceFlagValue={
											photosetSpace.flag_key_name
										}
										spaceId={photosetSpace.id}
										spaceIndex={spaceIndex}
										spaceName={photosetSpace.space.key_name}
									/>
								)}
							</Fragment>
						))}
				</PhotosetStepBodyContainer>
				<PageFooter
					centerSlot={
						<p>{t('photoset:curation.footer_step_label')}</p>
					}
					endSlot={
						<ButtonLegacy
							disabled={
								updateStepToCompleted.isPending ||
								updateStepToAllocationMutation.isPending
							}
							onClick={() => onCurationCompleted()}
							type='button'
							variant='primarySuccess'
						>
							{updateStepToCompleted.isPending && (
								<Spinner size={20} />
							)}
							{t('common:done')}
						</ButtonLegacy>
					}
					startSlot={
						<ButtonLegacy
							disabled={
								updateStepToAllocationMutation.isPending ||
								updateStepToCompleted.isPending
							}
							onClick={() =>
								updateStepToAllocationMutation.mutate(
									urlParams.id
								)
							}
							type='button'
							variant='tertiary'
						>
							<ArrowLeftIcon />
							{t('common:back')}
						</ButtonLegacy>
					}
				/>
			</PhotosetStepWrapper>

			{currentDetailImageIndex !== null && currentDetailSpace && (
				<GalleryCurationModal
					currentDetailImageIndex={currentDetailImageIndex}
					currentSpaceId={currentDetailSpace.id}
					onClose={onReduceImage}
					selectableImages={currentDetailSpace.images}
					setCurrentDetailImageIndex={setCurrentDetailImageIndex}
				/>
			)}
		</>
	)
}

PhotosetCurationStep.displayName = 'PhotosetCurationStep'
